import React from 'react';
import axios from "axios";
import { getJwtToken } from '../Common/Auth';
import { getApiURL } from "../Common/envVarsReader";
import Link from "@amzn/awsui-components-react/polaris/link";
import {
    Badge,
    Box,
    Container,
    Header,
    Button,
    Spinner,
    Table,
    TextFilter
} from '@amzn/awsui-components-react/polaris';
import '../../styles/center.scss';

/**
 * Parses CSV text into an array of objects using the header row as keys.
 */
function parseCsv(csvText) {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((header, idx) => {
            row[header] = values[idx] || '';
        });
        rows.push(row);
    }

    return rows;
}

export default function JobClosurePanel(props) {
    const [loading, setLoading] = React.useState(false);
    const [issues, setIssues] = React.useState([]);
    const [filteringText, setFilteringText] = React.useState('');
    const [filteredIssues, setFilteredIssues] = React.useState([]);

    React.useEffect(() => {
        loadClosureIssues(props.id);
    }, [props.id]);

    React.useEffect(() => {
        if (!filteringText) {
            setFilteredIssues(issues);
        } else {
            const filter = filteringText.toLowerCase();
            setFilteredIssues(issues.filter(issue =>
                (issue['Issue ID'] || '').toLowerCase().includes(filter) ||
                (issue['Closed'] || '').toLowerCase().includes(filter)
            ));
        }
    }, [filteringText, issues]);

    const loadClosureIssues = async (id) => {
        setLoading(true);

        const options = {
            headers: {
                'Authorization': `Bearer ${getJwtToken()}`,
                'Content-Type': 'application/json'
            },
        };

        try {
            // Find the issue_list.csv artifact for this job
            const artifactsResponse = await axios.get(`${getApiURL()}/job/artifacts?job=${id}`, options);
            const artifacts = artifactsResponse['data'];
            const csvArtifact = artifacts.find(a => a.key.includes('issue_list.csv'));

            if (csvArtifact) {
                const response = await axios.get(`${getApiURL()}/job/artifact?job=${id}&id=${csvArtifact.key}`, options);
                const parsed = parseCsv(response['data']);
                setIssues(parsed);
            }
        } catch (e) {
            console.log(e);
        }

        setLoading(false);
    };

    if (loading) {
        return (
            <Container header={<Header variant="h2">Closure Issues</Header>}>
                <div align="center">
                    <Spinner size="large" />
                </div>
            </Container>
        );
    }

    return (
        <Container
            header={
                <Header
                    variant="h2"
                    actions={
                        <Button
                            iconName="refresh"
                            onClick={(e) => { e.preventDefault(); loadClosureIssues(props.id); }}
                        />
                    }
                >
                    {"Closure Issues - " + issues.length}
                </Header>
            }
        >
            <Table
                items={filteredIssues}
                columnDefinitions={[
                    {
                        id: "id",
                        header: "Issue ID",
                        cell: item => item['Issue ID'] || '--'
                    },
                    {
                        id: "link",
                        header: "Link",
                        cell: item => item['Issue Link']
                            ? <Link external externalIconAriaLabel="Opens in a new tab" href={item['Issue Link']}>{item['Issue Link']}</Link>
                            : '--'
                    },
                    {
                        id: "closed",
                        header: "Status",
                        cell: item => {
                            const val = (item['Closed'] || '').toLowerCase();
                            if (val === 'true') return <Badge color="green">Closed</Badge>;
                            if (val === 'false') return <Badge color="red">Failed</Badge>;
                            return <Badge color="grey">N/A</Badge>;
                        }
                    }
                ]}
                filter={
                    <TextFilter
                        filteringPlaceholder="Find issue"
                        filteringText={filteringText}
                        onChange={({ detail }) => setFilteringText(detail.filteringText)}
                    />
                }
                empty={
                    <Box textAlign="center">
                        <b>No closure issues</b>
                        <p>No issue list found for this job.</p>
                    </Box>
                }
            />
        </Container>
    );
}
